import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { parseCookies, setCookie } from 'nookies';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { rehydrateStore, onSearchPageOpen, defineUserCoordinates, selectCity } from '../redux/actions';
import { Router } from '../routes';
import {
  version,
  cookieName,
  axiosInstance,
  makeUrl,
  getPluralProductName,
  slugify,
  startWithUpperCase,
  getSingularProductName,
  getAllProductNamesString,
} from '../utils';
import { Layout, Header, Footer } from '../components';
import { HotelParamsLine, HotelParamsLineMobile, SearchPageResults, DeviceTypeComponent } from '../containers';

const mapStateToProps = state => ({
  city: state.userPreferences.city,
  locationWasFetched: state.user.location.wasFetched,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  onPageOpen: onSearchPageOpen,
  getUserCoords: defineUserCoordinates,
  handleCitySelect: selectCity,
}, dispatch);

@connect(mapStateToProps, mapDispatchToProps)
class SearchPage extends Component {
  static propTypes = {
    searchData: PropTypes.instanceOf(Object),
    onPageOpen: PropTypes.func,
    canonicalUrl: PropTypes.string,
    meta: PropTypes.instanceOf(Object),
    getUserCoords: PropTypes.func,
    handleCitySelect: PropTypes.func,
    city: PropTypes.instanceOf(Object),
    locationWasFetched: PropTypes.bool,
  };

  static async getInitialProps(ctx) {
    const { query, req, reduxStore } = ctx;
    let cookie;
    let host;
    let token;

    if (req) {
      host = req.headers.host;
      const cookies = parseCookies(ctx);
      cookie = `sid=${parseCookies(ctx).sid};`;

      if (cookies.jwt) {
        token = cookies.jwt;
      }

      if (!query.product && query.category) {
        const parsedQuery = {
          version,
          product: getSingularProductName(startWithUpperCase(query.category)) === 'Day Pass' ? getAllProductNamesString() : getSingularProductName(startWithUpperCase(query.category)),
        };

        const userPreferences = parseCookies(ctx)[cookieName] || '{}';
        const parsedUserPreferences = JSON.parse(userPreferences);

        setCookie(
          ctx,
          cookieName,
          JSON.stringify({ ...parsedUserPreferences, ...parsedQuery }),
          { path: '/' },
        );

        reduxStore.dispatch(rehydrateStore({ ...parsedUserPreferences, ...parsedQuery }));
      } else {
        const parsedQuery = {
          version,
        };

        Object.keys(query).map(key => {
          if (key === 'hotel') {
            const [name, id] = query.hotel.split(',');

            parsedQuery.city = {
              label: name,
              value: name,
              type: 'hotel',
              id,
              market: query.marketName,
            };
          } else if (key === 'market') {
            const [name, state] = query.market.split(',');

            parsedQuery.city = {
              label: `${name},${state}`,
              value: name,
              type: 'city',
              id: null,
              market: query.marketName,
            };
          } else if (key === 'priceHi') {
            parsedQuery.price = {
              ...parsedQuery.price,
              priceHi: query.priceHi,
            };
          } else if (key === 'priceLo') {
            parsedQuery.price = {
              ...parsedQuery.price,
              priceLo: query.priceLo,
            };
          } else if (key === 'distanceHi') {
            parsedQuery.distance = {
              ...parsedQuery.distance,
              distanceHi: query.distanceHi,
            };
          } else if (key === 'distanceLo') {
            parsedQuery.distance = {
              ...parsedQuery.distance,
              distanceLo: query.distanceLo,
            };
          } else if (key !== 'marketName' && key !== 'category') {
            parsedQuery[key] = query[key];
          }

          return parsedQuery;
        });

        const userPreferences = parseCookies(ctx)[cookieName] || '{}';
        const parsedUserPreferences = JSON.parse(userPreferences);

        setCookie(
          ctx,
          cookieName,
          JSON.stringify({ ...parsedUserPreferences, ...parsedQuery }),
          { path: '/' },
        );

        reduxStore.dispatch(rehydrateStore({ ...parsedUserPreferences, ...parsedQuery }));
      }
    } else {
      host = window.location.host;
    }

    const { data: searchData } = await axiosInstance.get(
      `products/search?${makeUrl(query)}`,
      req && {
        headers: {
          'X-Access-Token': token || '',
          Cookie: cookie,
        },
      },
    );
    const sortedAmenitiesData = searchData.data.map(hotel => {
      const newHotel = { ...hotel };
      newHotel.amenities.sort(a => (Number(a) === 4 ? 1 : -1));

      return newHotel;
    });

    const sortedSearchData = { ...searchData, data: sortedAmenitiesData };

    const queryObject = {
      ...query,
      searchData: sortedSearchData,
    };

    const meta = {
      category: query.category,
      product: getSingularProductName(startWithUpperCase(query.category)),
      total: sortedSearchData.total,
      market: sortedSearchData.marketName,
      lowestPrice: sortedSearchData.lowestPrice && sortedSearchData.lowestPrice.toFixed(2),
    };

    const canonicalUrl = `https://${host}/s/${slugify(searchData.marketName || startWithUpperCase(query.marketName))}/${query.category}`;

    return { ...queryObject, canonicalUrl, meta, query };
  }

  constructor(props) {
    super(props);
    const { searchData } = props;
    const { data, total, isDefault, counter, marketName } = searchData;

    this.state = {
      loading: false,
      total: Number(total) || 0,
      hasMore: data.length < total,
      searchResults: data,
      counter,
      isDefault,
    };
  }

  async componentDidMount() {
    const { onPageOpen, getUserCoords, handleCitySelect } = this.props;
    Router.events.on('routeChangeComplete', this.handleRouteChange);

    if (!Router.query.marketName) {
      getUserCoords();
    }

    if (Router.query.marketName && !Router.query.market && !Router.query.hotel) {
      const { data } = await axiosInstance.get('cities_or_hotels?q=default');
      const city = data.cities.find(suggestion => startWithUpperCase(suggestion.market) === startWithUpperCase(Router.query.marketName));
      const { name, adminCode, lat, lon, market } = city;

      handleCitySelect({
        label: `${name}${adminCode ? `,${adminCode}` : ''}`,
        value: name,
        market,
        coordinates: {
          latitude: lat,
          longitude: lon,
        },
      });

      Router.pushRoute('search', {
        marketName: Router.query.marketName,
        category: Router.query.category,
        market: `${name},${adminCode}`,
      })
    }

    onPageOpen();
  }

  componentDidUpdate(prevProps) {
    const { city: prevCity, locationWasFetched: prevLocation } = prevProps;
    const { city: newCity, meta, locationWasFetched: newLocation } = this.props;

    if (!Router.query.marketName && newCity.type === 'city' && (JSON.stringify(prevCity.coordinates) !== JSON.stringify(newCity.coordinates) || prevLocation !== newLocation)) {
      Router.pushRoute('search', {
        marketName: slugify(newCity.market),
        category: meta.category || 'day-passes',
        market: newCity.label,
        product: meta.product === 'Day Pass' ? getAllProductNamesString() : meta.product,
      }, { shallow: true });
    }
  }

  componentWillUnmount() {
    Router.events.off('routeChangeComplete', this.handleRouteChange);
  }

  handleRouteChange = async () => {
    this.setState({
      loading: true,
    });

    const { data: searchData } = await axiosInstance.get(`products/search?${makeUrl(Router.query)}`);
    const { data, total, isDefault, counter } = searchData;
    this.setState({
      loading: false,
      total: Number(total) || 0,
      hasMore: data.length < total,
      searchResults: data,
      counter,
      isDefault,
    });
  };

  fetchMoreSearchResults = async page => {
    const { data: searchData } = await axiosInstance.get(`products/search?${makeUrl(Router.query)}&limit=10&offset=${page * 10}`);
    const { data, total } = searchData;

    const sortedAmenitiesData = data.map(hotel => {
      const newHotel = { ...hotel };

      newHotel.amenities.sort(a => (Number(a) === 4 ? 1 : -1));

      return newHotel;
    });

    this.setState(prevState => ({
      loading: false,
      total: Number(total) || 0,
      hasMore: [...prevState.searchResults, ...data].length < total,
      searchResults: [...prevState.searchResults, ...sortedAmenitiesData],
    }));
  };

  render() {
    const { fetchMoreSearchResults } = this;
    const { searchResults, loading, hasMore, isDefault, counter, total: newTotal } = this.state;
    const { meta, canonicalUrl, city: newCity, } = this.props;
    const { total, lowestPrice, market, category } = meta;

    return (
      <Layout
        heading={`${total === newTotal ? total : newTotal} Best Hotel ${startWithUpperCase(category)} in ${market} | Dayaxe.com`}
        description={`Book ${total} best hotel ${getPluralProductName(startWithUpperCase(category), 2).toLowerCase()} in ${market} on DayAxe.com for as little as $${lowestPrice}/guest. Explore amenities, reviews and photos, find the best pools in ${market}.`}
        canonical={canonicalUrl}
        url={canonicalUrl}
      >
        <div style={{ position: 'sticky', top: 0, zIndex: '5', backgroundColor: '#ffffff' }}>
          <Header isInnerPage />
          <DeviceTypeComponent
            desktop={<HotelParamsLine {...this.state} />}
            tablet={<HotelParamsLine {...this.state} />}
            mobile={<HotelParamsLineMobile {...this.state} total={newTotal} loading={loading} />}
          />
        </div>
        <SearchPageResults
          marketName={newCity.market}
          searchResults={searchResults}
          showsLosAngeles={isDefault}
          amountOfPeopleOnPage={counter}
          handlePageScrollLoading={fetchMoreSearchResults}
          hasMore={hasMore}
          loading={loading}
        />
        <Footer isInnerPage />
      </Layout>
    );
  }
}

export default SearchPage;
